import React, { FC } from "react";
import {
  DealPredictionsWrapper,
  FeatureDetails,
  PercentageIndicator,
  RecommendationItem,
  RecommendationsSection,
  ShareButtonWrapper,
  RiskFactorsSection,
  RiskItem,
  RiskScoreIndicator,
  RiskDetails,
} from "./DealPredictions.styled";
import ShareButton from "static/svgs/ShareButton";
import { RiskFactor } from "types";

interface RecommendedFeature {
  feature_name: string;
  impact: number;
  explanation: string;
}

interface TopRisk {
  category: string;
  checklist_id: string;
  reason: string;
  risk_meaningful: string;
  risk_priority: string;
  risk_score: number;
  theme_title: string | null;
  title: string;
}

interface RiskFactorData {
  _id: string;
  created_at: string;
  deal_id: string;
  organization_id: string;
  score: number;
  top_risks: TopRisk[];
  updated_at: string;
}

interface DealPredictionsProps {
  data: RecommendedFeature[] | undefined;
  riskFactors: RiskFactor | RiskFactorData | RiskFactor[] | RiskFactorData[] | undefined;
  overallScore: number | undefined;
}

const DealPredictions: FC<DealPredictionsProps> = ({ data, overallScore, riskFactors }) => {
  if (!data || !overallScore) {
    return null;
  }

  // Get top 3 risks by risk_score (negative values are highest risk)
  const topRisks: TopRisk[] = [];
  
  if (riskFactors) {
    // Handle case where riskFactors is a single object with top_risks array
    if (typeof riskFactors === 'object' && !Array.isArray(riskFactors) && 'top_risks' in riskFactors) {
      const riskData = riskFactors as RiskFactorData;
      if (Array.isArray(riskData.top_risks)) {
        topRisks.push(...riskData.top_risks);
      }
    }
    // Handle case where riskFactors is an array of objects with top_risks arrays
    else if (Array.isArray(riskFactors)) {
      for (const risk of riskFactors) {
        if ('top_risks' in risk && Array.isArray(risk.top_risks)) {
          topRisks.push(...risk.top_risks);
        }
      }
    }
  }
  
  // Sort by risk_score (negative values are highest risk) and take top 3
  const sortedTopRisks = topRisks
    .sort((a: TopRisk, b: TopRisk) => a.risk_score - b.risk_score)
    .slice(0, 3);

  return (
    <DealPredictionsWrapper>
      <ShareButtonWrapper>
        <p className="share-button-text">Share Recommendations</p>
        <ShareButton />
      </ShareButtonWrapper>
      <div className="score-section">
        <h2>Deal Success Score</h2>
        <div className="score-tracker">
          <div className="progress-bar" style={{ width: `${overallScore}%` }}>
            <span className="score-label">
              {overallScore < 10 ? "<10" : overallScore}%
            </span>
          </div>
        </div>
      </div>

      {sortedTopRisks.length > 0 && (
        <RiskFactorsSection>
          <h3 style={{ color: 'black' }}>Top Risk Factors</h3>
          {sortedTopRisks.map((risk, index) => (
            <RiskItem key={index}>
              <RiskScoreIndicator>
                {risk.risk_score.toFixed(2)}%
              </RiskScoreIndicator>
              <RiskDetails>
                <div className="risk-title">
                  {risk.title}
                </div>
                <div className="risk-description">
                  {risk.reason}
                </div>
              </RiskDetails>
            </RiskItem>
          ))}
        </RiskFactorsSection>
      )}

      <RecommendationsSection>
        <h3>Increase likelihood to win by taking these next steps:</h3>
        {data.map((feature, index) => (
          <RecommendationItem key={index}>
            <PercentageIndicator>
              +{feature.impact.toFixed(2)}%
            </PercentageIndicator>
            <FeatureDetails>
              <div className="feature-name">
                {feature.feature_name.split("_").join(" ")}
              </div>
              <div className="explanation">{feature.explanation}</div>
            </FeatureDetails>
          </RecommendationItem>
        ))}
      </RecommendationsSection>
    </DealPredictionsWrapper>
  );
};

export default DealPredictions;
